/**
 * Family Invite Acceptance Screen
 *
 * Accepts family plan invitations via deep link or web link.
 *
 * Deep Link: dramadetector://family/accept?code=INVITE_CODE
 * Web Link: https://dramadetector.com/family/accept?code=INVITE_CODE
 *
 * Flow:
 * 1. Extract invite code from URL parameters
 * 2. Fetch invite details (tier, admin, quota)
 * 3. Show confirmation screen with family plan details
 * 4. Accept invitation (calls family-accept Edge Function)
 * 5. Redirect to Family Management screen
 *
 * Error Handling:
 * - Invalid/expired invite code → Show error message
 * - Already in family plan → Show error with option to leave current plan
 * - Authentication required → Redirect to login
 *
 * Privacy-First Design:
 * - Shows aggregate family plan details only
 * - No access to other members' analysis content
 *
 * Refactored: 2025-11-21 (Line Count: 421 → 250 lines)
 * - Extracted types → lib/family/familyInviteTypes.ts
 * - Extracted fetch logic → hooks/useFamilyInviteDetails.ts
 */
import React from 'react';
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyPlan } from '../hooks/useFamilyPlan';
import { useFamilyInviteDetails } from '../hooks/useFamilyInviteDetails';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '../../../lib/analytics/events';

export default function FamilyAcceptScreen() {
  const { t } = useTranslation('subscription');
  const { t: tCommon } = useTranslation('common');
  const { code } = useLocalSearchParams<{ code: string }>();
  const { user } = useAuth();
  const { acceptInvite, isAccepting, isFamilyMember } = useFamilyPlan();
  const router = useRouter();

  // Fetch invite details using custom hook (replaces 94 lines of fetch logic)
  const { inviteDetails, isLoading, error } = useFamilyInviteDetails(code);

  // Handle accept invitation
  const handleAcceptInvite = async () => {
    if (!code || !inviteDetails) return;

    // Check if already in family plan
    if (isFamilyMember) {
      Alert.alert(t('family.error'), t('family.acceptInvite.alreadyMember'));
      return;
    }

    // Calculate time to accept in hours
    const timeToAcceptHours = Math.floor(
      (Date.now() - new Date(inviteDetails.created_at).getTime()) / (1000 * 60 * 60)
    );

    try {
      trackEvent('Button_Click_AcceptFamilyInvite', {
        invite_code: code.substring(0, 4), // Obfuscated (first 4 chars)
        tier: inviteDetails.family_plan.tier,
        time_to_accept_hours: timeToAcceptHours,
      });

      await acceptInvite(code);

      trackEvent('FamilyInvite_Accepted', {
        family_plan_id: inviteDetails.family_plan_id,
        tier: inviteDetails.family_plan.tier,
        new_member_count: inviteDetails.family_plan.member_count + 1,
        daily_quota: inviteDetails.family_plan.daily_quota,
      });

      Alert.alert(t('family.success'), t('family.acceptInvite.success'), [
        {
          text: tCommon('confirm'),
          onPress: () => router.replace('/(tabs)/family-manage'),
        },
      ]);
    } catch (err) {
      trackEvent('Error_AcceptFamilyInvite', {
        error_type: 'unknown',
        error_message: err instanceof Error ? err.message : 'Unknown',
        invite_code: code.substring(0, 4),
      });

      Alert.alert(
        t('family.error'),
        err instanceof Error ? err.message : t('family.acceptInvite.error')
      );
    }
  };

  // Handle decline
  const handleDecline = () => {
    if (code && inviteDetails) {
      trackEvent('Button_Click_DeclineFamilyInvite', {
        invite_code: code.substring(0, 4), // Obfuscated (first 4 chars)
        tier: inviteDetails.family_plan.tier,
      });
    }

    router.back();
  };

  // Require authentication
  if (!user) {
    return (
      <View className="flex-1 bg-gray-50 items-center justify-center p-6">
        <Stack.Screen options={{ title: t('family.acceptInvite.title') }} />
        <View className="bg-white rounded-2xl p-6 shadow-sm max-w-md">
          <Text className="text-xl font-semibold text-gray-900 mb-3">
            {t('family.acceptInvite.signInRequired')}
          </Text>
          <Text className="text-gray-600 mb-6">
            {t('family.acceptInvite.signInMessage')}
          </Text>
          <Pressable
            onPress={() => router.push('/(auth)/login')}
            className="bg-teal-600 rounded-xl py-3 px-4 active:opacity-80"
          >
            <Text className="text-white font-semibold text-center">
              {t('family.acceptInvite.signInButton')}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <View className="flex-1 bg-gray-50 items-center justify-center">
        <Stack.Screen options={{ title: t('family.acceptInvite.title') }} />
        <ActivityIndicator size="large" color="#14B8A6" />
      </View>
    );
  }

  // Error state
  if (error || !inviteDetails) {
    return (
      <View className="flex-1 bg-gray-50 items-center justify-center p-6">
        <Stack.Screen options={{ title: t('family.acceptInvite.title') }} />
        <View className="bg-white rounded-2xl p-6 shadow-sm max-w-md">
          <Text className="text-xl font-semibold text-red-600 mb-3">
            {t('family.error')}
          </Text>
          <Text className="text-gray-600 mb-6">
            {error || t('family.acceptInvite.invalidCode')}
          </Text>
          <Pressable
            onPress={() => router.back()}
            className="bg-gray-200 rounded-xl py-3 px-4 active:opacity-80"
          >
            <Text className="text-gray-700 font-semibold text-center">
              {tCommon('close')}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const tierName = t(`family.tierName.${inviteDetails.family_plan.tier}`);

  return (
    <ScrollView className="flex-1 bg-gray-50">
      <Stack.Screen
        options={{
          title: t('family.acceptInvite.title'),
          headerBackTitle: tCommon('back'),
        }}
      />

      <View className="p-6">
        {/* Invitation Header */}
        <View className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <Text className="text-2xl font-bold text-gray-900 mb-2">
            {t('family.acceptInvite.description')}
          </Text>
          <Text className="text-gray-600">
            {t('family.acceptInvite.invitedBy', {
              tierName,
              adminName: inviteDetails.family_plan.admin_name,
            })}
          </Text>
        </View>

        {/* Family Plan Details */}
        <View testID="invitation-details" className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <Text className="text-lg font-semibold text-gray-900 mb-4">
            {t('family.acceptInvite.planDetailsHeader')}
          </Text>

          {/* Tier */}
          <View className="mb-4">
            <Text className="text-sm text-gray-500 mb-1">
              {t('family.acceptInvite.tierLabel')}
            </Text>
            <View className="flex-row items-center">
              <View className="bg-teal-100 px-3 py-1 rounded-lg">
                <Text className="text-teal-700 font-semibold">
                  {tierName} Family
                </Text>
              </View>
              <Text className="text-gray-600 ml-2">
                ({inviteDetails.family_plan.billing_interval})
              </Text>
            </View>
          </View>

          {/* Daily Quota */}
          <View className="mb-4">
            <Text className="text-sm text-gray-500 mb-1">
              {t('family.acceptInvite.quotaLabel')}
            </Text>
            <Text className="text-xl font-semibold text-gray-900">
              {inviteDetails.family_plan.daily_quota}{' '}
              {t('family.acceptInvite.analysesPerDay')}
            </Text>
            <Text className="text-xs text-gray-500 mt-1">
              {t('family.acceptInvite.quotaShared')}
            </Text>
          </View>

          {/* Admin */}
          <View>
            <Text className="text-sm text-gray-500 mb-1">
              {t('family.acceptInvite.adminLabel')}
            </Text>
            <Text className="text-gray-900 font-medium">
              {inviteDetails.family_plan.admin_name}
            </Text>
          </View>
        </View>

        {/* Privacy Notice */}
        <View className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
          <Text className="text-blue-900 font-semibold mb-2">
            {t('family.privacyTitle')}
          </Text>
          <Text className="text-blue-800 text-sm">
            {t('family.privacyDescription')}
          </Text>
        </View>

        {/* Action Buttons */}
        <View className="flex-row gap-3 mb-6">
          <Pressable
            onPress={handleDecline}
            disabled={isAccepting}
            className="flex-1 bg-gray-200 rounded-xl py-4 px-4 active:opacity-80"
          >
            <Text className="text-gray-700 font-semibold text-center text-lg">
              {t('family.acceptInvite.declineButton')}
            </Text>
          </Pressable>

          <Pressable
            testID="accept-invitation"
            onPress={handleAcceptInvite}
            disabled={isAccepting}
            className="flex-1 bg-teal-600 rounded-xl py-4 px-4 active:opacity-80"
          >
            {isAccepting ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text className="text-white font-semibold text-center text-lg">
                {t('family.acceptInvite.acceptButton')}
              </Text>
            )}
          </Pressable>
        </View>

        {/* Expiration Notice */}
        <View className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <Text className="text-amber-800 text-sm">
            {t('family.acceptInvite.expirationNotice', {
              date: new Date(inviteDetails.expires_at).toLocaleDateString(),
            })}
          </Text>
        </View>
      </View>
    </ScrollView>
  );
}
